import { useEffect, useRef } from "react";
import { useRocketanks } from "@/lib/stores/useRocketanks";
import { useAudio } from "@/lib/stores/useAudio";
import { useColyseus } from "@/lib/colyseus/useColyseus";
import { useShallow } from "zustand/react/shallow";
import type { MusicContext } from "@/lib/audio/audioConfig";
import type { MusicPhase } from "@/lib/audio/musicConductor";

export function MusicAdapter() {
  // Use individual primitive selectors to avoid reference changes
  const phase = useRocketanks((state) => state.phase);
  const wave = useRocketanks((state) => state.wave);
  const terrainTheme = useRocketanks((state) => state.terrain?.theme ?? "industrial");
  const isMultiplayer = useRocketanks((state) => state.isMultiplayer);
  const killsThisWave = useRocketanks((state) => state.killsThisWave);
  const gameMode = useRocketanks((state) => state.gameMode);
  // [BR-MP] No multiplayer o gameMode do store local fica em "roguelike";
  // o modo real vem das regras da sala — sem isto a trilha do Battle Royale
  // nunca tocava em partidas BR multiplayer.
  const mpIsBattleRoyale = useColyseus((s) => s.rules?.gameMode === "battleRoyale");
  
  // Get stable primitive values from playerTank
  const playerHealth = useRocketanks((state) => state.playerTank?.health ?? 100);
  const playerMaxHealth = useRocketanks((state) => state.playerTank?.maxHealth ?? 100);
  
  // Get enemy counts as primitives
  const aliveEnemies = useRocketanks((state) => 
    state.enemyTanks.filter((e) => e.isAlive).length
  );
  const totalEnemies = useRocketanks((state) => state.enemyTanks.length);
  const hasBoss = useRocketanks((state) => 
    state.enemyTanks.some((e) => e.isAlive && e.enemyType === "boss")
  );

  // Use useShallow for audio functions to avoid creating new object references
  const { startMusic, stopMusic, updateAdaptiveMusic, ensureMusicConductor } = useAudio(
    useShallow((s) => ({
      startMusic: s.startMusic,
      stopMusic: s.stopMusic,
      updateAdaptiveMusic: s.updateAdaptiveMusic,
      ensureMusicConductor: s.ensureMusicConductor,
    }))
  );
  
  // Compute derived values
  const playerHealthPct = playerMaxHealth > 0 ? playerHealth / playerMaxHealth : 1;
  const adaptivePhase: MusicPhase = phase === "storyPrologue" ? "storyIntro" : phase;

  // Track if conductor is initialized
  const conductorInitialized = useRef(false);

  useEffect(() => {
    if (!conductorInitialized.current) {
      ensureMusicConductor();
      conductorInitialized.current = true;
    }
  }, [ensureMusicConductor]);

  useEffect(() => {
    // MusicAdapter controla apenas música de batalha e história
    // Menu/Credits/gameOver são controlados por Menu.tsx
    if (phase === "playing") {
      // Escolhe a música baseada no modo de jogo
      let musicContext: MusicContext = 'battle';
      if (gameMode === "battleRoyale" || (isMultiplayer && mpIsBattleRoyale)) {
        musicContext = 'battle_royale';
      } else if (gameMode === "genai") {
        musicContext = 'battle_genai';
      }
      startMusic(musicContext);
    } else if (phase === "storyIntro" || phase === "storyPlaying") {
      startMusic('story');
    } else if (phase === "storyPrologue") {
      // Para o prólogo cinematográfico, usar música de story mas com mais calma
      startMusic('story');
    } else if (phase === "story" || phase === "menu") {
      // Quando sai totalmente do modo história ou vai pro menu, para a música
      stopMusic();
    }
    // storyVictory: NÃO para a música aqui - deixa o stinger de vitória tocar
    // O stinger faz ducking automático na música
    // A música será parada quando o usuário voltar ao menu (phase === "story")
    // menu, gameOver, paused, waveComplete, genAIVictory: não muda música aqui
    // Menu.tsx controla a música do menu/credits

    // Cleanup: garante que a música para se o componente desmontar
    // (ex: HMR, navegação inesperada, saída do modo história)
    return () => {
      stopMusic();
    };
  }, [phase, gameMode, isMultiplayer, mpIsBattleRoyale, startMusic, stopMusic]);

  useEffect(() => {
    updateAdaptiveMusic({
      phase: adaptivePhase,
      wave,
      terrain: terrainTheme,
      isMultiplayer,
      aliveEnemies,
      totalEnemies,
      playerHealthPct,
      killsThisWave,
      hasBoss,
    });
  }, [
    adaptivePhase,
    wave,
    terrainTheme,
    isMultiplayer,
    aliveEnemies,
    totalEnemies,
    hasBoss,
    playerHealthPct,
    killsThisWave,
    updateAdaptiveMusic,
  ]);

  return null;
}
